import { useEffect, useRef, useLayoutEffect, useState } from 'react'

const InsularObserver = options => {
    const listeners = new WeakMap()

    const callback = entries => {
        entries.forEach(entry => {
            const targetListeners = listeners.get(entry.target)
            targetListeners.forEach(listener => listener(entry))
        })
    }

    const observer = new IntersectionObserver(callback, options)

    function observe(target, listener) {
        if (!target) {
            return Function.prototype
        }
        if (!listeners.has(target)) {
            listeners.set(target, [])
        }

        const targetListeners = listeners.get(target)
        targetListeners.push(listener)
        observer.observe(target)

        return function unobserve() {
            const idx = targetListeners.indexOf(listener)
            targetListeners.splice(idx, 1)

            if (targetListeners.length === 0) {
                listeners.delete(target)
                return observer.unobserve && observer.unobserve(target)
            }
        }
    }

    return observe
}

const Observers = new Map()

/**
 * Hook to create an intersection observer. elements can register with this intersection observe via useIntersectionObservedElement
 *
 * @param {Object} options
 * @param {string} options.id - required id to identify the observer/root element
 * @param {Object} options.rootElementRef - ref of the root element
 * @param {string} options.rootSelector - selector of the root element
 */
function useCreateIntersectionObserver({ id, rootElementRef, rootSelector }) {
    if (typeof id !== 'string' || id.length === 0) {
        console.error('Defining an ID is mandatory for useCreateIntersectionObserver')
    }

    useLayoutEffect(() => {
        const rootElement = rootSelector ? document.querySelector(rootSelector) : rootElementRef.current
        if (rootElement && id) {
            const observe = InsularObserver({
                root: rootElement
            })

            Observers.set(id, observe)
        }
    }, [rootElementRef, rootSelector, id])
}

/**
 * Register an element to be observed via the IntersectionObserver specified via the id.
 * You can either use the returned boolean or register your own callback for more complex information
 * on the entry.
 *
 * @param {string} id - required id to identify the observer/root element
 * @param {Object} target - ref of the root element
 * @param {Function} [callback] - optional callback that is triggered on intersection updates and passed the entry object
 * @param {Object} [options] - optional option object
 * @property {boolean} [options.hiddenByDefault] - option for whether component should be visible per default
 * @returns {boolean} - whether the element is currently inside the root element or not
 */
function useIntersectionObservedElement(id, target, callback, { hiddenByDefault } = {}) {
    const callbackRef = useRef(callback)
    const [intersects, setIntersects] = useState(!hiddenByDefault)
    const cleanup = useRef()
    const renderCount = useRef(0)
    const currentTarget = useRef()
    const previousTarget = useRef()
    currentTarget.current = target && Object.hasOwn(target, 'current') ? target.current : target

    useLayoutEffect(() => {
        if (currentTarget?.current && currentTarget.current !== previousTarget.current) {
            previousTarget.current = currentTarget.current
            renderCount.current++
            const current = renderCount.current
            Promise.resolve().then(() => {
                if (current === renderCount.current) {
                    const observe = Observers.get(id)
                    if (typeof observe === 'function') {
                        typeof cleanup.current === 'function' && cleanup.current()
                        if (currentTarget.current instanceof Element) {
                            const unobserve = observe(currentTarget.current, entry => {
                                setIntersects(entry.isIntersecting)
                                typeof callbackRef.current === 'function' && callbackRef.current(entry)
                            })

                            cleanup.current = () => {
                                cleanup.current = null
                                unobserve()
                            }
                        }
                    }
                }
            })
        }
    })

    useEffect(
        () => () => {
            typeof cleanup.current === 'function' && cleanup.current()
        },
        [cleanup]
    )

    return intersects
}

export { useCreateIntersectionObserver, useIntersectionObservedElement }
